import React, { useState, useEffect } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Users, Shield, FileText, Plus, Search, Download, Link2, ClipboardCheck, MapPin, Camera, ShoppingCart, Table } from 'lucide-react';
import { formatDate, formatTime, today } from '../utils/dateFormat';
import { MediaViewer } from '../components/shared/MediaComponents';

const TABS = [
  { key: 'users', label: 'Users', icon: Users },
  { key: 'hr', label: 'HR / Attendance', icon: ClipboardCheck },
  { key: 'master', label: 'Master View', icon: Table },
  { key: 'compliance', label: 'Daily Compliance', icon: Shield },
  { key: 'assignments', label: 'Assignments', icon: Link2 },
  { key: 'permissions', label: 'Permissions', icon: Shield },
  { key: 'audit', label: 'Audit Log', icon: FileText },
  { key: 'exports', label: 'Exports', icon: Download },
];

export default function AdminPanel() {
  const [tab, setTab] = useState('users');
  const { user } = useAuth();
  const HR_TABS = ['users', 'hr', 'master', 'compliance', 'assignments'];
  const visibleTabs = user?.role === 'hr' ? TABS.filter(t => HR_TABS.includes(t.key)) : TABS;
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
      <div className="flex gap-1 overflow-x-auto border-b pb-px scrollbar-hide">
        {visibleTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition ${tab === t.key ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'users' && <UsersTab />}
      {tab === 'hr' && <HRTab />}
      {tab === 'master' && <MasterHRTab />}
      {tab === 'compliance' && <ComplianceTab />}
      {tab === 'assignments' && <AssignmentsTab />}
      {tab === 'permissions' && <PermissionsTab />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'exports' && <ExportsTab />}
    </div>
  );
}

/* ═══════════════ USERS ═══════════════ */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const fetchUsers = () => {
    const params = {};
    if (search) params.search = search;
    if (roleFilter) params.role = roleFilter;
    api.get('/users', { params }).then(r => setUsers(r.data.users)).catch(() => {});
  };
  useEffect(() => { fetchUsers(); }, [roleFilter]);

  const toggleActive = async (u) => {
    try {
      if (u.is_active) { await api.put(`/users/${u.id}/deactivate`, { reason: 'Admin action' }); toast.success(`${u.name} deactivated`); }
      else { await api.put(`/users/${u.id}/reactivate`); toast.success(`${u.name} reactivated`); }
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const resetPwd = async (u) => {
    if (!window.confirm(`Reset password for ${u.name}?`)) return;
    try { const { data } = await api.put(`/users/${u.id}/reset-password`); toast.success(`New password: ${data.temporaryPassword}`, { duration: 10000 }); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchUsers()} placeholder="Search..." className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm outline-none" />
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white">
          <option value="">All Roles</option>
          {['admin', 'md', 'manager', 'supervisor', 'purchase', 'accounts', 'hr', 'factory'].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm"><Plus size={16} /> Add</button>
      </div>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr>{['Name', 'Email', 'Role', 'Status', 'Last Login', 'Actions'].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>)}</tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2.5 font-medium">{u.name}</td>
                <td className="px-4 py-2.5 text-gray-500">{u.email}</td>
                <td className="px-4 py-2.5"><span className="text-xs bg-gray-100 px-2 py-0.5 rounded capitalize">{u.role}</span></td>
                <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-2.5 text-xs text-gray-400">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('en-IN') : 'Never'}</td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-2">
                    <button onClick={() => toggleActive(u)} className={`text-xs px-2 py-1 rounded ${u.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}>{u.is_active ? 'Deactivate' : 'Activate'}</button>
                    <button onClick={() => resetPwd(u)} className="text-xs px-2 py-1 rounded text-amber-600 hover:bg-amber-50">Reset Pwd</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No users</p>}
      </div>
      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchUsers(); }} />}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const isHR = user?.role === 'hr';
  const ROLE_OPTIONS = isHR ? ['supervisor'] : ['admin', 'md', 'manager', 'supervisor', 'purchase', 'accounts', 'hr', 'factory'];
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'supervisor' });
  const [saving, setSaving] = useState(false); const [tempPwd, setTempPwd] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const { data } = await api.post('/users', form); if (data.temporaryPassword) setTempPwd(data.temporaryPassword); else { toast.success('Created'); onCreated(); } } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };
  if (tempPwd) return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"><div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center">
      <h2 className="text-lg font-semibold mb-3">User Created</h2><p className="text-sm text-gray-600 mb-2">Temporary password:</p>
      <p className="text-lg font-mono bg-gray-100 p-3 rounded-lg select-all">{tempPwd}</p>
      <p className="text-xs text-gray-400 mt-2">Must change on first login</p>
      <button onClick={onCreated} className="mt-4 px-6 py-2 bg-brand-600 text-white rounded-lg text-sm">Done</button>
    </div></div>
  );
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"><div className="bg-white rounded-2xl w-full max-w-md">
      <div className="px-6 py-4 border-b flex items-center justify-between"><h2 className="text-lg font-semibold">Add User</h2><button onClick={onClose} className="text-gray-400 text-xl">&times;</button></div>
      <form onSubmit={submit} className="p-6 space-y-4">
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full Name *" required className="w-full px-3 py-2 border rounded-lg text-sm" />
        <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email *" required className="w-full px-3 py-2 border rounded-lg text-sm" />
        <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" className="w-full px-3 py-2 border rounded-lg text-sm" />
        <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} disabled={isHR} className="w-full px-3 py-2 border rounded-lg text-sm">
          {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="flex gap-3"><button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm text-gray-600">Cancel</button><button type="submit" disabled={saving} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm disabled:opacity-50">{saving ? 'Creating...' : 'Create'}</button></div>
      </form>
    </div></div>
  );
}

/* ═══════════════ MASTER HR VIEW ═══════════════ */
function MasterHRTab() {
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [staffFilter, setStaffFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchData = () => {
    setLoading(true);
    api.get(`/export/master-hr?from=${dateFrom}&to=${dateTo}&format=json`)
      .then(r => { setRows(r.data.rows || []); setStaff(r.data.staff || []); setLoading(false); })
      .catch(() => { toast.error('Failed to load'); setLoading(false); });
  };
  useEffect(() => { fetchData(); }, []);

  const downloadCSV = () => {
    api.get(`/export/master-hr?from=${dateFrom}&to=${dateTo}`, { responseType: 'blob' }).then(r => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `master_hr_${dateFrom}_${dateTo}.csv`; a.click();
      toast.success('Downloaded');
    }).catch(() => toast.error('Export failed'));
  };

  const filtered = rows.filter(r => {
    if (staffFilter && r.name !== staffFilter) return false;
    if (statusFilter === 'compliant' && r.compliant !== 'YES') return false;
    if (statusFilter === 'incomplete' && r.compliant !== 'NO') return false;
    if (statusFilter === 'absent' && r.checked_in !== 'No') return false;
    return true;
  });

  // Summary stats
  const totalRows = filtered.length;
  const checkedInCount = filtered.filter(r => r.checked_in === 'Yes').length;
  const compliantCount = filtered.filter(r => r.compliant === 'YES').length;
  const totalLabour = filtered.reduce((s, r) => s + (r.labour_count || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold text-gray-800">Master HR View — All Staff, All Data</h3>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-[10px] text-gray-500 uppercase font-bold block mb-1">From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase font-bold block mb-1">To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase font-bold block mb-1">Staff</label>
            <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.name}>{s.name} ({s.role})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase font-bold block mb-1">Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">All</option>
              <option value="compliant">Compliant</option>
              <option value="incomplete">Incomplete</option>
              <option value="absent">Absent</option>
            </select>
          </div>
          <button onClick={fetchData} disabled={loading} className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Loading...' : 'Load Data'}
          </button>
          <button onClick={downloadCSV} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border p-3 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">Total Rows</p><p className="text-xl font-black text-gray-800">{totalRows}</p></div>
        <div className="bg-white rounded-xl border p-3 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">Checked In</p><p className="text-xl font-black text-green-600">{checkedInCount}</p></div>
        <div className="bg-white rounded-xl border p-3 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">Compliant</p><p className="text-xl font-black text-brand-600">{compliantCount}</p></div>
        <div className="bg-white rounded-xl border p-3 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">Total Labour</p><p className="text-xl font-black text-purple-600">{totalLabour}</p></div>
      </div>

      {/* Data Table */}
      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin h-6 w-6 border-4 border-brand-600 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {['Name', 'Role', 'Date', 'In', 'In Time', 'Out', 'Out Time', 'Hours', 'Status', 'GPS', 'Updates', 'Update Details', 'Labour', 'Labour Details', 'Expense', 'Expense Details', 'Compliant'].map(h =>
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={i} className={`border-t hover:bg-gray-50 ${r.compliant === 'NO' ? 'bg-red-50/40' : ''}`}>
                  <td className="px-3 py-2 font-medium text-gray-800">{r.name}</td>
                  <td className="px-3 py-2"><span className="text-xs bg-gray-100 px-2 py-0.5 rounded capitalize">{r.role}</span></td>
                  <td className="px-3 py-2 text-gray-600">{r.date}</td>
                  <td className="px-3 py-2">{r.checked_in === 'Yes' ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-400">✗</span>}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{r.check_in_time}</td>
                  <td className="px-3 py-2">{r.checked_out === 'Yes' ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-400">✗</span>}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{r.check_out_time}</td>
                  <td className="px-3 py-2 text-xs font-medium">{r.hours !== '-' ? `${r.hours}h` : '-'}</td>
                  <td className="px-3 py-2 text-xs capitalize">{r.morning_status}</td>
                  <td className="px-3 py-2"><span className={`text-xs font-bold ${r.gps_pings > 5 ? 'text-green-600' : r.gps_pings > 0 ? 'text-amber-600' : 'text-red-400'}`}>{r.gps_pings}</span></td>
                  <td className="px-3 py-2 text-center"><span className={`text-xs font-bold ${r.daily_updates_count > 0 ? 'text-green-600' : 'text-red-400'}`}>{r.daily_updates_count}</span></td>
                  <td className="px-3 py-2 text-[10px] text-gray-500 max-w-[200px] truncate" title={r.daily_updates_detail}>{r.daily_updates_detail}</td>
                  <td className="px-3 py-2 text-center"><span className={`text-xs font-bold ${r.labour_count > 0 ? 'text-green-600' : 'text-red-400'}`}>{r.labour_count}</span></td>
                  <td className="px-3 py-2 text-[10px] text-gray-500 max-w-[200px] truncate" title={r.labour_detail}>{r.labour_detail}</td>
                  <td className="px-3 py-2 text-xs font-medium">{r.expense_total}</td>
                  <td className="px-3 py-2 text-[10px] text-gray-500 max-w-[200px] truncate" title={r.expense_detail}>{r.expense_detail}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded ${r.compliant === 'YES' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {r.compliant}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No data for selected range</p>}
        </div>
      )}
    </div>
  );
}

/* ═══════════════ HR / ATTENDANCE ═══════════════ */
function HRTab() {
  const [date, setDate] = useState(today());
  const [attendance, setAttendance] = useState([]);
  const [notCheckedIn, setNotCheckedIn] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [userERs, setUserERs] = useState([]);

  const fetchHR = () => {
    setLoading(true);
    api.get('/attendance/today').then(r => {
      setAttendance(r.data.attendance || []);
      setNotCheckedIn(r.data.notCheckedIn || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchHR(); api.get('/users').then(r => setUsers(r.data.users || [])).catch(() => {}); }, []);

  const viewProfile = (userId) => {
    setSelectedUser(userId);
    setProfileLoading(true);
    api.get(`/attendance/user-profile/${userId}`).then(r => { setProfile(r.data); setProfileLoading(false); api.get(`/expense-requests/user/${userId}?is_personal=false&status=all`).then(er => setUserERs(er.data.expenses || [])).catch(() => {}); }).catch(() => setProfileLoading(false));
  };

  const refetchProfile = (from, to) => {
    if (!selectedUser) return;
    api.get(`/attendance/user-profile/${selectedUser}?from=${from}&to=${to}`).then(r => setProfile(r.data)).catch(() => {});
  };

  const downloadExcel = () => {
    const now = new Date();
    const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`;
    api.get(`/export/attendance?from=${from}&to=${to}`, { responseType: 'blob' }).then(r => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `attendance_${from}_${to}.csv`; a.click();
      toast.success('Downloaded');
    }).catch(() => toast.error('Failed'));
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin h-6 w-6 border-4 border-brand-600 border-t-transparent rounded-full" /></div>;

  // ═══ FULL EMPLOYEE PROFILE ═══
  if (selectedUser && profile) {
    return <EmployeeProfile profile={profile} onBack={() => { setSelectedUser(null); setProfile(null); }} onRefetch={refetchProfile} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold text-gray-800">Team Attendance</h3>
        <div className="flex gap-2">
          {/* User dropdown */}
          <select onChange={e => { if (e.target.value) viewProfile(e.target.value); }} className="px-3 py-2 border rounded-lg text-sm bg-white" defaultValue="">
            <option value="">View staff profile...</option>
            {users.filter(u => u.role !== 'admin').map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
          <button onClick={downloadExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm"><Download size={14} /> Export CSV</button>
        </div>
      </div>

      {/* Not checked in alert */}
      {notCheckedIn.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="font-medium text-red-700 text-sm">Not Checked In Today ({notCheckedIn.length})</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {notCheckedIn.map(u => (
              <span key={u.id} className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-full">{u.name} ({u.role})</span>
            ))}
          </div>
        </div>
      )}

      {/* Live Location Map */}
      <HRLiveMap />

      {/* Attendance table — names are clickable */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>{['Name', 'Role', 'Check In', 'Check Out', 'Status', 'Hours', 'Location'].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>)}</tr>
          </thead>
          <tbody>
            {attendance.map(a => {
              const hours = a.check_in_at && a.check_out_at ? ((new Date(a.check_out_at) - new Date(a.check_in_at)) / 3600000).toFixed(1) : '-';
              const lat = a.check_in_lat ? Number(a.check_in_lat).toFixed(4) : '-';
              const lng = a.check_in_lng ? Number(a.check_in_lng).toFixed(4) : '-';
              return (
                <tr key={a.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2.5"><button onClick={() => viewProfile(a.user?.id)} className="font-medium text-brand-600 hover:underline">{a.user?.name}</button></td>
                  <td className="px-4 py-2.5"><span className="text-xs bg-gray-100 px-2 py-0.5 rounded capitalize">{a.user?.role}</span></td>
                  <td className="px-4 py-2.5 text-green-600">{a.check_in_at ? formatTime(a.check_in_at) : '-'}</td>
                  <td className="px-4 py-2.5 text-red-500">{a.check_out_at ? formatTime(a.check_out_at) : <span className="text-amber-500">Still in</span>}</td>
                  <td className="px-4 py-2.5 capitalize text-xs">{a.morning_status?.replace('_', ' ') || '-'}</td>
                  <td className="px-4 py-2.5">{hours !== '-' ? `${hours}h` : '-'}</td>
                  <td className="px-4 py-2.5">
                    {lat !== '-' ? (
                      <a href={`https://www.google.com/maps?q=${a.check_in_lat},${a.check_in_lng}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-brand-600 hover:underline">{lat}, {lng} ↗</a>
                    ) : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {attendance.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No attendance records for today</p>}
      </div>
    </div>
  );
}

/* ═══════════════ EMPLOYEE PROFILE (FULL 360° VIEW) ═══════════════ */
function EmployeeProfile({ profile, onBack, onRefetch }) {
  const [pTab, setPTab] = useState('attendance');
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const u = profile.user;
  const s = profile.stats;
  const BACKEND = (process.env.REACT_APP_API_URL || '/api').replace('/api', '');

  const handleDateChange = (from, to) => { setDateFrom(from); setDateTo(to); onRefetch(from, to); };

  const profileTabs = [
    { key: 'attendance', label: 'Attendance' },
    { key: 'photos', label: 'Photos' },
    { key: 'locations', label: 'Locations' },
    { key: 'updates', label: 'Daily Updates' },
    { key: 'labour', label: 'Labour' },
    { key: 'purchase', label: 'Purchase Orders' },
    { key: 'expenses', label: 'Expenses' },
    { key: 'leaves', label: 'Leaves' },
    { key: 'sites', label: 'Assigned Sites' },
  ];

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-brand-600 text-sm font-bold hover:underline">← Back to HR</button>

      {/* Header */}
      <div className="bg-white rounded-xl border p-5 flex items-center gap-4">
        <div className="w-14 h-14 bg-brand-600 text-white rounded-full flex items-center justify-center text-xl font-black">{u?.name?.[0]}</div>
        <div className="flex-1">
          <h2 className="text-xl font-black">{u?.name}</h2>
          <p className="text-sm text-gray-500 capitalize">{u?.role} · {u?.email} {u?.phone && `· ${u.phone}`}</p>
          <p className="text-[10px] text-gray-400">Joined: {formatDate(u?.created_at)}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <div className="bg-white rounded-xl border p-2.5 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">Days</p><p className="text-lg font-black text-brand-600">{s?.totalDays}</p></div>
        <div className="bg-white rounded-xl border p-2.5 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">At Site</p><p className="text-lg font-black text-green-600">{s?.onSite}</p></div>
        <div className="bg-white rounded-xl border p-2.5 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">Travel</p><p className="text-lg font-black text-amber-600">{s?.traveling}</p></div>
        <div className="bg-white rounded-xl border p-2.5 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">Leave</p><p className="text-lg font-black text-red-600">{s?.onLeave}</p></div>
        <div className="bg-white rounded-xl border p-2.5 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">Hours</p><p className="text-lg font-black text-purple-600">{s?.totalHours}h</p></div>
        <div className="bg-white rounded-xl border p-2.5 text-center"><p className="text-[9px] text-gray-500 uppercase font-bold">GPS Pings</p><p className="text-lg font-black text-blue-600">{s?.totalPings || 0}</p></div>
      </div>

      {/* Date range + export */}
      <div className="flex items-center gap-2 flex-wrap">
        <input type="date" value={dateFrom} onChange={e => handleDateChange(e.target.value, dateTo)} className="px-2 py-1.5 border rounded-lg text-xs" />
        <span className="text-xs text-gray-400">to</span>
        <input type="date" value={dateTo} onChange={e => handleDateChange(dateFrom, e.target.value)} className="px-2 py-1.5 border rounded-lg text-xs" />
        <button onClick={() => { api.get(`/export/compliance?from=${dateFrom}&to=${dateTo}&user_id=${u.id}`, { responseType: 'blob' }).then(r => { const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `${u.name}_${dateFrom}_${dateTo}.csv`; a.click(); toast.success('Downloaded'); }).catch(() => toast.error('Failed')); }} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold"><Download size={12} className="inline mr-1" />Export CSV</button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto scrollbar-hide border-b">
        {profileTabs.map(t => (
          <button key={t.key} onClick={() => setPTab(t.key)} className={`px-3 py-2 text-xs font-bold whitespace-nowrap border-b-2 transition ${pTab === t.key ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500'}`}>{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      {pTab === 'attendance' && (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr>{['Date', 'In', 'Out', 'Status', 'Hours', 'Pings', 'GPS'].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">{h}</th>)}</tr></thead>
            <tbody>
              {(profile.attendance || []).map(a => {
                const hours = a.check_in_at && a.check_out_at ? ((new Date(a.check_out_at) - new Date(a.check_in_at)) / 3600000).toFixed(1) : '-';
                return (
                  <tr key={a.id} className="border-t">
                    <td className="px-3 py-2 font-medium text-xs">{formatDate(a.date)}</td>
                    <td className="px-3 py-2 text-green-600 text-xs">{a.check_in_at ? formatTime(a.check_in_at) : '-'}</td>
                    <td className="px-3 py-2 text-red-500 text-xs">{a.check_out_at ? formatTime(a.check_out_at) : <span className="text-amber-500">Still in</span>}</td>
                    <td className="px-3 py-2 capitalize text-[10px]">{a.morning_status?.replace('_', ' ') || '-'}</td>
                    <td className="px-3 py-2 text-xs">{hours !== '-' ? `${hours}h` : '-'}</td>
                    <td className="px-3 py-2 text-xs font-bold">{(a.location_trail || []).length}</td>
                    <td className="px-3 py-2">{a.check_in_lat ? <a href={`https://www.google.com/maps?q=${a.check_in_lat},${a.check_in_lng}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-brand-600 hover:underline">↗ Map</a> : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(!profile.attendance?.length) && <p className="text-center text-gray-400 text-sm py-6">No records</p>}
        </div>
      )}

      {pTab === 'photos' && (
        <div className="space-y-3">
          <p className="text-sm font-bold">Uploaded Photos & Files ({(profile.media || []).length})</p>
          {(profile.media || []).length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(profile.media || []).map(m => {
                const url = m.s3_url?.startsWith('/') ? `${BACKEND}${m.s3_url}` : m.s3_url;
                const isImg = m.file_type === 'image';
                return (
                  <div key={m.id} className="bg-white rounded-xl border overflow-hidden">
                    {isImg && url ? (
                      <img src={url} alt={m.file_name} className="w-full h-32 object-cover cursor-pointer" onClick={() => window.open(url, '_blank')} />
                    ) : (
                      <div className="w-full h-32 bg-gray-100 flex items-center justify-center"><FileText size={24} className="text-gray-400" /></div>
                    )}
                    <div className="p-2">
                      <p className="text-[10px] text-gray-600 truncate">{m.file_name}</p>
                      <p className="text-[9px] text-gray-400">{m.entity_type} · {formatDate(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-gray-400 text-sm">No photos uploaded in this period</p>}
        </div>
      )}

      {pTab === 'locations' && (
        <div className="space-y-3">
          <p className="text-sm font-bold">GPS Location History</p>
          {(profile.attendance || []).filter(a => (a.location_trail || []).length > 0).map(a => (
            <div key={a.id} className="bg-white rounded-xl border p-3">
              <p className="text-xs font-bold mb-2">{formatDate(a.date)} — {(a.location_trail || []).length} pings</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {(a.location_trail || []).map((p, j) => (
                  <div key={j} className="flex items-center gap-2 text-[11px]">
                    <span className="text-gray-400 w-16">{new Date(p.time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                    <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">{Number(p.lat).toFixed(5)}, {Number(p.lng).toFixed(5)} ↗</a>
                    {p.type && <span className="text-[8px] bg-blue-100 text-blue-600 px-1 rounded">{p.type}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {(profile.attendance || []).filter(a => (a.location_trail || []).length > 0).length === 0 && <p className="text-gray-400 text-sm">No GPS data in this period</p>}
        </div>
      )}

      {pTab === 'updates' && (
        <div className="space-y-2">
          <p className="text-sm font-bold">Daily Updates ({(profile.dailyUpdates || []).length})</p>
          {(profile.dailyUpdates || []).map(du => (
            <div key={du.id} className="bg-white rounded-xl border p-3 hazard-stripe">
              <div className="flex justify-between"><p className="text-xs font-bold">{du.project?.name || 'Unknown'}</p><p className="text-[10px] text-gray-400">{formatDate(du.update_date)}</p></div>
              <p className="text-sm text-gray-700 mt-1">{du.content_text}</p>
            </div>
          ))}
          {!(profile.dailyUpdates?.length) && <p className="text-gray-400 text-sm">No daily updates</p>}
        </div>
      )}

      {pTab === 'labour' && (
        <div className="space-y-2">
          <p className="text-sm font-bold">Labour Entries ({(profile.labourEntries || []).length})</p>
          {(profile.labourEntries || []).map(l => (
            <div key={l.id} className="bg-white rounded-xl border p-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{l.contractor_name} <span className="text-xs text-gray-400">({l.trade || 'General'})</span></p>
                <p className="text-[10px] text-gray-400">{l.project?.name} · {formatDate(l.date)}</p>
              </div>
              <div className="w-10 h-10 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center font-bold">{l.worker_count}</div>
            </div>
          ))}
          {!(profile.labourEntries?.length) && <p className="text-gray-400 text-sm">No labour entries</p>}
        </div>
      )}

      {pTab === 'purchase' && (
        <div className="space-y-2">
          <p className="text-sm font-bold">Purchase Orders ({(profile.purchaseOrders || []).length})</p>
          {(profile.purchaseOrders || []).map(po => {
            const sc = po.status === 'delivered' ? 'bg-green-100 text-green-700' : po.status === 'approved' || po.status === 'ordered' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700';
            return (
              <div key={po.id} className="bg-white rounded-xl border p-3">
                <div className="flex justify-between items-start">
                  <div><p className="text-sm font-bold">{po.po_number}</p><p className="text-[10px] text-gray-400">{po.project?.name} · {formatDate(po.created_at)}</p></div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${sc}`}>{po.status?.replace(/_/g, ' ')}</span>
                </div>
                {(po.items || []).map((it, i) => <p key={i} className="text-xs text-gray-600 mt-1">{it.material} — {it.quantity} {it.unit}</p>)}
              </div>
            );
          })}
          {!(profile.purchaseOrders?.length) && <p className="text-gray-400 text-sm">No purchase orders</p>}
        </div>
      )}

      {pTab === 'expenses' && (
        <div className="space-y-2">
          <p className="text-sm font-bold">Site Expenses ({(profile.expenses || []).length})</p>
          {(profile.expenses || []).map(e => (
            <div key={e.id} className="bg-white rounded-xl border p-3 flex justify-between">
              <div><p className="text-sm font-medium">{e.material}</p><p className="text-[10px] text-gray-400">{e.project?.name} · {formatDate(e.expense_date)}</p></div>
              <p className="text-sm font-bold">₹{Number(e.total_price || 0).toLocaleString('en-IN')}</p>
            </div>
          ))}
          {!(profile.expenses?.length) && <p className="text-gray-400 text-sm">No site expenses</p>}

          {/* Expense Requests (approval-based) */}
          <p className="text-sm font-bold mt-4 pt-3 border-t">Expense Requests ({userERs.length})</p>
          {userERs.map(er => (
            <div key={er.id} className="bg-white rounded-xl border p-3 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{er.er_number}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${er.approval_status === 'approved' ? 'bg-green-100 text-green-700' : er.approval_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{er.approval_status?.replace(/_/g, ' ')}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 capitalize">{er.category}</span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5">{er.description}</p>
                <p className="text-[10px] text-gray-400">{er.project?.name || 'Personal'} · {formatDate(er.expense_date || er.created_at)}</p>
              </div>
              <p className="text-sm font-bold">₹{Number(er.amount || 0).toLocaleString('en-IN')}</p>
            </div>
          ))}
          {userERs.length === 0 && <p className="text-gray-400 text-sm">No expense requests</p>}
        </div>
      )}

      {pTab === 'leaves' && (
        <div className="space-y-2">
          <p className="text-sm font-bold">Leave Requests ({(profile.leaves || []).length})</p>
          {(profile.leaves || []).map(l => (
            <div key={l.id} className="bg-white rounded-xl border p-3 flex justify-between">
              <div><p className="text-sm font-medium capitalize">{l.leave_type || 'Leave'}</p><p className="text-[10px] text-gray-400">{formatDate(l.start_date)} — {formatDate(l.end_date)}</p></div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold self-start ${l.status === 'approved' ? 'bg-green-100 text-green-700' : l.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{l.status}</span>
            </div>
          ))}
          {!(profile.leaves?.length) && <p className="text-gray-400 text-sm">No leave requests</p>}
        </div>
      )}

      {pTab === 'sites' && (
        <div className="space-y-2">
          <p className="text-sm font-bold">Assigned Sites ({(profile.projects || []).length})</p>
          {(profile.projects || []).map(p => (
            <div key={p.id} className="bg-white rounded-xl border p-3 flex justify-between items-center">
              <div><p className="text-sm font-medium">{p.name}</p><p className="text-[10px] text-gray-400">{p.location} · {p.status}</p></div>
              <p className="text-lg font-black text-brand-600">{p.progress_percentage || 0}%</p>
            </div>
          ))}
          {!(profile.projects?.length) && <p className="text-gray-400 text-sm">No assigned sites</p>}
        </div>
      )}
    </div>
  );
}

/* ═══════════════ LIVE MAP ═══════════════ */
function HRLiveMap() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = () => {
      api.get('/attendance/live-locations').then(r => { setLocations(r.data.locations || []); setLoading(false); }).catch(() => setLoading(false));
    };
    fetch();
    const i = setInterval(fetch, 30000);
    return () => clearInterval(i);
  }, []);

  if (loading) return null;

  const hasLocations = locations.length > 0;
  const avgLat = hasLocations ? locations.reduce((s, l) => s + l.lat, 0) / locations.length : 13.08;
  const avgLng = hasLocations ? locations.reduce((s, l) => s + l.lng, 0) / locations.length : 80.27;

  // Build Leaflet map HTML with markers
  const markersJS = locations.map(l =>
    `L.marker([${l.lat},${l.lng}]).addTo(map).bindPopup('<b>${(l.user?.name||'').replace(/'/g,'')}</b><br>${l.user?.role||''}<br>Since ${l.check_in_at ? new Date(l.check_in_at).toLocaleTimeString() : ''}').openPopup()`
  ).join(';\n');

  const boundsJS = locations.map(l => `[${l.lat},${l.lng}]`).join(',');
  const mapHtml = `<!DOCTYPE html><html><head>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<style>body{margin:0}#map{width:100%;height:100vh}</style>
</head><body><div id="map"></div><script>
var map=L.map('map').setView([${avgLat},${avgLng}],14);
L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',{attribution:'CartoDB'}).addTo(map);
${markersJS};
var bounds=L.latLngBounds([${boundsJS}]);if(bounds.isValid())map.fitBounds(bounds,{padding:[40,40],maxZoom:15})
L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',{attribution:'CartoDB'}).addTo(map);
${markersJS}
</script></body></html>`;

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm text-gray-800 flex items-center gap-2">
          <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse inline-block" />
          Live Staff Locations
        </h3>
        <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{locations.length} active</span>
      </div>

      {/* Leaflet Map with markers */}
      <div className="relative h-80 bg-gray-100">
        <iframe title="Live Map" srcDoc={mapHtml} className="w-full h-full border-0" loading="lazy" sandbox="allow-scripts" />
      </div>

      {/* Staff cards */}
      <div className="p-3 space-y-2">
        {locations.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No staff currently checked in with location</p>}
        {locations.map((loc, i) => (
          <a key={i} href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              <div>
                <p className="text-sm font-medium">{loc.user?.name}</p>
                <p className="text-[10px] text-gray-400 capitalize">{loc.user?.role} · Since {new Date(loc.check_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-brand-600">{loc.lat.toFixed(4)}, {loc.lng.toFixed(4)} ↗</p>
              {loc.trail?.length > 1 && <p className="text-[10px] text-gray-400">{loc.trail.length} location pings</p>}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════ DAILY COMPLIANCE ═══════════════ */
function ComplianceTab() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trailUser, setTrailUser] = useState(null);
  const [trail, setTrail] = useState([]);
  const [viewMode, setViewMode] = useState('daily'); // daily or range

  const fetchDaily = () => {
    setLoading(true);
    api.get(`/attendance/compliance?date=${date}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { if (viewMode === 'daily') fetchDaily(); }, [date, viewMode]);

  const viewTrail = (userId, userName) => {
    setTrailUser(userName);
    api.get(`/attendance/location-trail/${userId}?date=${date}`).then(r => setTrail(r.data.trails || [])).catch(() => setTrail([]));
  };

  const downloadCSV = () => {
    const from = viewMode === 'daily' ? date : dateFrom;
    const to = viewMode === 'daily' ? date : dateTo;
    api.get(`/export/compliance?from=${from}&to=${to}`, { responseType: 'blob' }).then(r => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `compliance_${from}_${to}.csv`; a.click();
      toast.success('Downloaded');
    }).catch(() => toast.error('Export failed'));
  };

  const downloadUserCSV = (userId) => {
    const from = dateFrom; const to = dateTo;
    api.get(`/export/compliance?from=${from}&to=${to}&user_id=${userId}`, { responseType: 'blob' }).then(r => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `compliance_user_${from}_${to}.csv`; a.click();
      toast.success('Downloaded');
    }).catch(() => toast.error('Export failed'));
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin h-6 w-6 border-4 border-brand-600 border-t-transparent rounded-full" /></div>;

  const s = data?.summary || {};
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-bold text-gray-800 uppercase tracking-wide text-sm">Daily Compliance</h3>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button onClick={() => setViewMode('daily')} className={`px-3 py-1.5 rounded text-xs font-bold ${viewMode === 'daily' ? 'bg-white shadow' : ''}`}>Day</button>
            <button onClick={() => setViewMode('range')} className={`px-3 py-1.5 rounded text-xs font-bold ${viewMode === 'range' ? 'bg-white shadow' : ''}`}>Range</button>
          </div>
          {viewMode === 'daily' ? (
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="px-3 py-1.5 border rounded-lg text-sm" />
          ) : (
            <div className="flex gap-1 items-center">
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2 py-1.5 border rounded-lg text-xs" />
              <span className="text-xs text-gray-400">to</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2 py-1.5 border rounded-lg text-xs" />
            </div>
          )}
          <button onClick={downloadCSV} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold"><Download size={12} /> Export CSV</button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-bold">Total Staff</p><p className="text-2xl font-black">{s.total || 0}</p></div>
        <div className="bg-white rounded-xl border p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-bold">Checked In</p><p className="text-2xl font-black text-green-600">{s.checkedIn || 0}</p></div>
        <div className="bg-white rounded-xl border p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-bold">Checked Out</p><p className="text-2xl font-black text-blue-600">{s.checkedOut || 0}</p></div>
        <div className="bg-white rounded-xl border p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-bold">Daily Updates</p><p className="text-2xl font-black text-purple-600">{s.addedUpdates || 0}</p></div>
        <div className="bg-white rounded-xl border p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-bold">Fully Compliant</p><p className="text-2xl font-black text-brand-600">{s.fullyCompliant || 0}/{s.total || 0}</p></div>
      </div>

      {/* Staff table */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>{['Name', 'Role', 'Check In', 'Check Out', 'Updates', 'Labour', 'Expenses', 'Status', 'Trail', 'Export'].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(data?.compliance || []).map(r => (
              <tr key={r.user.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{r.user.name}</td>
                <td className="px-3 py-2"><span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded capitalize">{r.user.role}</span></td>
                <td className="px-3 py-2">{r.checkedIn ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-500 font-bold">✗</span>}</td>
                <td className="px-3 py-2">{r.checkedOut ? <span className="text-green-600 font-bold">✓</span> : <span className="text-amber-500">—</span>}</td>
                <td className="px-3 py-2 font-bold">{r.dailyUpdates}</td>
                <td className="px-3 py-2 font-bold">{r.labourEntries}</td>
                <td className="px-3 py-2 font-bold">{r.expenses}</td>
                <td className="px-3 py-2">{r.compliant ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded font-bold">✓</span> : <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold">✗</span>}</td>
                <td className="px-3 py-2"><button onClick={() => viewTrail(r.user.id, r.user.name)} className="text-[10px] text-brand-600 font-bold hover:underline">📍</button></td>
                <td className="px-3 py-2"><button onClick={() => downloadUserCSV(r.user.id)} className="text-[10px] text-green-600 font-bold hover:underline">CSV</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!data?.compliance || data.compliance.length === 0) && <p className="text-center text-gray-400 text-sm py-6">No staff data</p>}
      </div>

      {/* Location trail */}
      {trailUser && (
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold">📍 Location Trail — {trailUser} ({date})</p>
            <button onClick={() => setTrailUser(null)} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
          </div>
          {trail.length === 0 ? <p className="text-gray-400 text-sm">No location data for this date</p> : trail.map((t, i) => (
            <div key={i} className="mb-3 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-bold">Session {i + 1}: {t.check_in_at ? new Date(t.check_in_at).toLocaleTimeString() : '—'} — {t.check_out_at ? new Date(t.check_out_at).toLocaleTimeString() : 'Still in'}</p>
              {t.trail && t.trail.length > 0 ? (
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {t.trail.map((p, j) => (
                    <div key={j} className="flex items-center gap-2 text-[11px]">
                      <span className="text-gray-400">{new Date(p.time).toLocaleTimeString()}</span>
                      <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">{Number(p.lat).toFixed(5)}, {Number(p.lng).toFixed(5)} ↗</a>
                      {p.type && <span className="text-[9px] bg-blue-100 text-blue-600 px-1 rounded">{p.type}</span>}
                    </div>
                  ))}
                </div>
              ) : <p className="text-[11px] text-gray-400 mt-1">No GPS pings recorded. Pings appear every 10 minutes while app is open.</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════ ASSIGNMENTS ═══════════════ */
function AssignmentsTab() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [roleOnProject, setRoleOnProject] = useState('supervisor');
  const [isPrimary, setIsPrimary] = useState(false);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.projects)).catch(() => {});
    api.get('/users').then(r => setUsers(r.data.users)).catch(() => {});
  }, []);

  const assign = async () => {
    if (!selectedProject || !selectedUser) { toast.error('Select project and user'); return; }
    try {
      await api.post(`/projects/${selectedProject}/assign`, { user_id: selectedUser, role_on_project: roleOnProject, is_primary: isPrimary });
      toast.success('Assigned!');
      // Refresh project data
      api.get('/projects').then(r => setProjects(r.data.projects));
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const unassign = async (projectId, userId) => {
    if (!window.confirm('Unassign this user?')) return;
    try { await api.put(`/projects/${projectId}/unassign/${userId}`, { reason: 'Admin action' }); toast.success('Unassigned'); api.get('/projects').then(r => setProjects(r.data.projects)); } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <p className="font-semibold text-sm">Assign User to Project</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} className="px-3 py-2 border rounded-lg text-sm col-span-2 md:col-span-1">
            <option value="">Select Project</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="">Select User</option>
            {users.filter(u => ['supervisor', 'manager'].includes(u.role) && u.is_active).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
          <select value={roleOnProject} onChange={e => setRoleOnProject(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="supervisor">Supervisor</option>
            <option value="manager">Manager</option>
          </select>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} className="rounded" /> Primary
          </label>
          <button onClick={assign} className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm">Assign</button>
        </div>
      </div>

      <div className="space-y-3">
        {projects.filter(p => p.assignedUsers?.length > 0).map(p => (
          <div key={p.id} className="bg-white rounded-xl border p-4">
            <p className="font-medium text-sm mb-2">{p.name} <span className="text-gray-400 font-normal">— {p.location}</span></p>
            <div className="space-y-1">
              {p.assignedUsers?.map(u => (
                <div key={u.id} className="flex items-center justify-between py-1.5 border-t first:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-[10px] font-bold">{u.name[0]}</div>
                    <span className="text-sm">{u.name}</span>
                    <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{u.ProjectAssignment?.role_on_project}</span>
                    {u.ProjectAssignment?.is_primary && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Primary</span>}
                  </div>
                  <button onClick={() => unassign(p.id, u.id)} className="text-xs text-red-500 hover:bg-red-50 px-2 py-1 rounded">Remove</button>
                </div>
              ))}
            </div>
          </div>
        ))}
        {projects.filter(p => p.assignedUsers?.length > 0).length === 0 && <p className="text-center text-gray-400 text-sm py-6">No assignments yet</p>}
      </div>
    </div>
  );
}

/* ═══════════════ PERMISSIONS ═══════════════ */
function PermissionsTab() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data.users)).catch(() => {});
    api.get('/users/roles/defaults').then(r => setRoleDefaults(r.data.defaults)).catch(() => {});
  }, []);

  const PERM_KEYS = [
    { key: 'project.view', label: 'View Projects' },
    { key: 'project.create', label: 'Create Projects' },
    { key: 'project.edit', label: 'Edit Projects' },
    { key: 'project.delete', label: 'Delete Projects' },
    { key: 'project.view_budget', label: 'View Budget' },
    { key: 'measurement.submit', label: 'Submit Measurement' },
    { key: 'measurement.approve', label: 'Approve Measurement', locked: true },
    { key: 'payment.add', label: 'Add Payment', locked: true },
    { key: 'payment.view', label: 'View Payments', locked: true },
    { key: 'leave.approve', label: 'Approve Leave' },
    { key: 'purchase.view', label: 'View Purchase Orders' },
    { key: 'purchase.create', label: 'Create Purchase Orders' },
    { key: 'expense.view', label: 'View Expenses' },
    { key: 'expense.create', label: 'Create Expenses' },
    { key: 'expense.edit', label: 'Edit Expenses' },
    { key: 'issue.close', label: 'Close Issues' },
    { key: 'drawing.upload', label: 'Upload Drawings' },
    { key: 'chat.pin_message', label: 'Pin Messages' },
    { key: 'report.export', label: 'Export Reports' },
    { key: 'incentive.configure', label: 'Configure Incentives', locked: true },
  ];

  const getEffectiveValue = (user, key) => {
    if (user.permissions && key in user.permissions) return user.permissions[key];
    return (roleDefaults[user.role] || {})[key] || false;
  };

  const togglePermission = async (user, key) => {
    const current = getEffectiveValue(user, key);
    const newPerms = { ...user.permissions, [key]: !current };
    setSaving(true);
    try {
      await api.put(`/users/${user.id}/permissions`, { permissions: newPerms });
      toast.success('Updated');
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, permissions: newPerms } : u));
      setSelectedUser(prev => prev ? { ...prev, permissions: newPerms } : prev);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border p-4">
        <p className="font-semibold text-sm mb-3">Select User to Edit Permissions</p>
        <select value={selectedUser?.id || ''} onChange={e => setSelectedUser(users.find(u => u.id === e.target.value) || null)} className="w-full px-3 py-2 border rounded-lg text-sm">
          <option value="">Choose user...</option>
          {users.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
        </select>
      </div>

      {selectedUser && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="font-medium text-sm">{selectedUser.name} <span className="text-gray-400">— {selectedUser.role}</span></p>
            <p className="text-[10px] text-gray-400">Toggle overrides role defaults. 🔒 = hard-locked to specific roles.</p>
          </div>
          <div className="divide-y">
            {PERM_KEYS.map(p => {
              const val = getEffectiveValue(selectedUser, p.key);
              const isDefault = !(selectedUser.permissions && p.key in selectedUser.permissions);
              return (
                <div key={p.key} className="flex items-center justify-between px-4 py-2.5">
                  <div>
                    <p className="text-sm">{p.label} {p.locked && '🔒'}</p>
                    <p className="text-[10px] text-gray-400">{p.key}{isDefault ? ' (role default)' : ' (override)'}</p>
                  </div>
                  <button
                    onClick={() => !p.locked && togglePermission(selectedUser, p.key)}
                    disabled={p.locked || saving}
                    className={`w-12 h-6 rounded-full transition relative ${val ? 'bg-green-500' : 'bg-gray-300'} ${p.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition ${val ? 'left-6' : 'left-0.5'}`} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ AUDIT ═══════════════ */
function AuditTab() {
  const [logs, setLogs] = useState([]); const [total, setTotal] = useState(0); const [page, setPage] = useState(0);
  const fetchLogs = () => api.get(`/audit?limit=30&offset=${page * 30}`).then(r => { setLogs(r.data.logs); setTotal(r.data.total); }).catch(() => {});
  useEffect(() => { fetchLogs(); }, [page]);
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">{total} entries</p>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr>{['Time', 'User', 'Action', 'Entity', 'Details'].map(h => <th key={h} className="text-left px-4 py-2 text-xs font-medium text-gray-500">{h}</th>)}</tr></thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id} className="border-t">
                <td className="px-4 py-2 text-xs text-gray-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString('en-IN')}</td>
                <td className="px-4 py-2">{l.user?.name}</td>
                <td className="px-4 py-2"><span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{l.action}</span></td>
                <td className="px-4 py-2 text-gray-500">{l.entity_type}</td>
                <td className="px-4 py-2 text-xs text-gray-400 max-w-xs truncate">{l.new_values ? JSON.stringify(l.new_values).substring(0, 80) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between">
        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1.5 text-sm bg-gray-100 rounded disabled:opacity-50">Prev</button>
        <span className="text-xs text-gray-400">Page {page + 1} / {Math.ceil(total / 30) || 1}</span>
        <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 30 >= total} className="px-3 py-1.5 text-sm bg-gray-100 rounded disabled:opacity-50">Next</button>
      </div>
    </div>
  );
}

/* ═══════════════ EXPORTS ═══════════════ */
function ExportsTab() {
  const dl = (url, name) => {
    api.get(url, { responseType: 'blob' }).then(r => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
      toast.success(`Downloaded ${name}`);
    }).catch(() => toast.error('Export failed'));
  };
  const now = new Date();
  const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`;

  const items = [
    { label: 'Projects', desc: 'All projects with status and budget', fn: () => dl('/export/projects', 'projects.csv') },
    { label: 'Attendance', desc: 'This month records', fn: () => dl(`/export/attendance?from=${from}&to=${to}`, 'attendance.csv') },
    { label: 'Master HR', desc: 'All staff: check-ins, updates, labour, expenses in one sheet', fn: () => dl(`/export/master-hr?from=${from}&to=${to}`, `master_hr_${from}_${to}.csv`) },
    { label: 'Purchase Orders', desc: 'All POs with vendor + status', fn: () => dl('/export/purchase-orders', 'purchase_orders.csv') },
    { label: 'Incentives', desc: 'Current month incentives', fn: () => dl('/export/incentives', 'incentives.csv') },
    { label: 'Audit Log', desc: 'Complete audit trail', fn: () => dl('/export/audit', 'audit_log.csv') },
  ];

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map(e => (
        <div key={e.label} className="bg-white rounded-xl border p-4">
          <h3 className="font-medium text-gray-800">{e.label}</h3>
          <p className="text-xs text-gray-500 mt-1 mb-3">{e.desc}</p>
          <button onClick={e.fn} className="flex items-center gap-2 px-3 py-1.5 bg-brand-600 text-white rounded-lg text-xs"><Download size={14} /> CSV</button>
        </div>
      ))}
    </div>
  );
}
